import React from 'react'
import classNames from 'classnames'
import { Icon } from '@zambezi/sdk/icons'

import format from '@apiEnabler/ob-common/lib/currency-formatter'
import { formatDate } from '@apiEnabler/ob-common/lib/date-utils'
import { getStatusName } from '@apiEnabler/ob-common/lib/standing-order-consents'
import DateTimeWarning from '@apiEnabler/ob-common/lib/view/DateTimeWarning'
import PaymentDetailItem from '@apiEnabler/ob-common/lib/view/PaymentDetailItem'

import FromAndTo from '@apiEnabler/ob-common/lib/view/stores/FromAndTo'
import StandingOrderReferenceNumbers from '@apiEnabler/ob-common/lib/view/stores/PaymentReferenceNumbers'
import PageTitle from './PageTitle'
import NavBar, { NavItem } from './NavBar'
import HistoryCard from './HistoryCard'

import style from './StandingOrder.module.css'

export default function StandingOrder({ standingOrder, brand }) {
  return (
    <section className={`${style.content} js-standing-order`}>
      <NavBar
        items={[
          new NavItem({
            label: 'Standing Orders',
            link: '/recurring-payments/standing-orders',
          }),
        ]}
        padded
      />
      <header className={classNames(style.paymentHeader, 'js-header')}>
        <PageTitle
          consentType="standingOrder"
          status={getStatusName(standingOrder)}
          label={format(
            standingOrder.getIn(['payload', 'currency']),
            standingOrder.getIn(['payload', 'nextPaymentAmount'])
          )}
          secondaryText={`to ${standingOrder.getIn([
            'payload',
            'creditor',
            'account',
            'customerName',
          ])}`}
        />
        <PaymentInformation standingOrder={standingOrder} />
      </header>
      <div>
        <div className="zb-card js-standing-orders-account-card">
          <FromAndTo
            debtor={standingOrder.getIn(['payload', 'debtor'])}
            creditor={standingOrder.getIn(['payload', 'creditor'])}
          />
          <div className={style.cardBody}>
            <PaymentDetailItem
              label="First payment"
              className="js-standing-order-details-first-payment-row"
              value={format(
                standingOrder.getIn(['payload', 'currency']),
                standingOrder.getIn(['payload', 'firstPaymentAmount'])
              )}
              valueAnnotation={formatDate(
                standingOrder.getIn(['payload', 'firstPaymentDate'])
              )}
            />
            <PaymentDetailItem
              label={standingOrder.getIn(['payload', 'displayFrequency'])}
              className="js-standing-order-details-recurring-payment-row"
              value={format(
                standingOrder.getIn(['payload', 'currency']),
                standingOrder.getIn(
                  ['payload', 'recurringPaymentAmount'],
                  standingOrder.getIn(['payload', 'firstPaymentAmount'])
                )
              )}
            />
            {standingOrder.getIn(['payload', 'finalPaymentDate']) && (
              <PaymentDetailItem
                label="Final payment"
                className="js-standing-order-details-final-payment-row"
                value={format(
                  standingOrder.getIn(['payload', 'currency']),
                  standingOrder.getIn(
                    ['payload', 'finalPaymentAmount'],
                    standingOrder.getIn(
                      ['payload', 'recurringPaymentAmount'],
                      standingOrder.getIn(['payload', 'firstPaymentAmount'])
                    )
                  )
                )}
                valueAnnotation={formatDate(
                  standingOrder.getIn(['payload', 'finalPaymentDate'])
                )}
              />
            )}
          </div>
          <StandingOrderReferenceNumbers
            transactionRef={standingOrder.getIn([
              'payload',
              'paymentTransactionId',
            ])}
            payeeRef={standingOrder.getIn(['payload', 'reference'])}
          />
        </div>
        <div
          className={classNames(style.history, 'js-payment-history js-header')}
        >
          <h2 className={style.historyHeading}>
            <span>History</span>
          </h2>
          <div className="zb-card">
            {standingOrder.get('history').map((record, i) => (
              <HistoryCard
                key={i}
                history={record}
                className={classNames(`js-history-card-${i}`)}
              />
            ))}
            <div className={style.cardBody}>
              <div
                className={classNames(
                  style.leftPaddedCard,
                  'js-standing-order-cancel-information'
                )}
              >
                <span>You can cancel a standing order payment in your </span>
                <a
                  className={classNames(style.linkWithIcon, 'js-link')}
                  href={brand.getIn(['links', 'Home'])}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`More information about ${brand.get(
                    'home'
                  )}, an external link that will open in a new tab`}
                >
                  <span>{brand.get('home')}</span>
                  <Icon name="popout-small" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </div>
        <DateTimeWarning />
      </div>
    </section>
  )
}

function PaymentInformation({ standingOrder }) {
  const date = standingOrder.getIn(['payload', 'paymentDate'])
  const label = standingOrder.getIn(['payload', 'paymentLabel'])
  return (
    <div className={classNames(style.dateLabel, 'js-payment-status-date')}>
      {` payment ${label} at `}
      <time>{formatDate(date)}</time>
    </div>
  )
}
